import { css, CSSResultGroup, LitElement } from "lit";
import { property, query } from "lit/decorators.js";

import { dateConverter, DesignToken, focusWhenReady } from "@m3e/web/core";

import { clampDate, sameDate } from "./utils";

/**
 * A base implementation for a view in a calendar. This class must be inherited.
 * @internal
 */
export abstract class CalendarViewElementBase extends LitElement {
  /** The styles of the element. */
  static override styles: CSSResultGroup = css`
    :host {
      display: inline-block;
      user-select: none;
      vertical-align: top;
    }
    .visually-hidden {
      position: absolute;
      appearance: none;
      visibility: hidden;
      border: none;
      outline: none;
      overflow: hidden;
      left: 0;
      height: 1px;
      width: 1px;
      margin: -1px;
      padding: 0;
      white-space: nowrap;
    }
    table {
      border-collapse: collapse;
      border-spacing: 0;
      width: calc(48px * 7);
    }
    td,
    th {
      font: inherit;
      text-align: center;
      padding: unset;
    }
    td {
      box-sizing: border-box;
      height: 48px;
      padding: 4px;
      position: relative;
      color: var(--m3e-calendar-item-color, ${DesignToken.color.onSurface});
    }
    .item,
    .item > span {
      position: relative;
    }
    .item {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: inherit;
      outline: none;
      width: 100%;
      height: 100%;
      border-radius: ${DesignToken.shape.corner.full};
    }
    .item:not([aria-disabled]) {
      cursor: pointer;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
    .touch {
      position: absolute;
      height: 48px;
      width: 48px;
      margin: auto;
    }
    td:not(:has(.item[aria-disabled])).selected {
      color: var(--m3e-calendar-item-selected-color, ${DesignToken.color.onPrimary});
      --m3e-ripple-color: var(--m3e-calendar-item-selected-ripple-color, ${DesignToken.color.onPrimary});
      --m3e-state-layer-hover-color: var(--m3e-calendar-item-selected-hover-color, ${DesignToken.color.onPrimary});
      --m3e-state-layer-focus-color: var(--m3e-calendar-item-selected-focus-color, ${DesignToken.color.onPrimary});
    }
    td:not(:has(.item[aria-disabled])).selected .state-layer {
      background-color: var(--m3e-calendar-item-selected-container-color, ${DesignToken.color.primary});
    }
    td.current:not(.selected):not(.special):not(.range-start):not(.range-end) {
      color: var(--m3e-calendar-item-current-outline-color, ${DesignToken.color.primary});
    }
    td.current:not(.selected):not(.special):not(.range-start):not(.range-end) .state-layer {
      outline-style: solid;
      outline-offset: -1px;
      outline-width: var(--m3e-calendar-item-current-outline-thickness, 1px);
      outline-color: var(--m3e-calendar-item-current-outline-color, ${DesignToken.color.primary});
    }
    td:has(.item[aria-disabled]) {
      color: color-mix(
        in srgb,
        var(--m3e-calendar-item-disabled-color, ${DesignToken.color.onSurface})
          var(--m3e-calendar-item-disabled-color-opacity, 38%),
        transparent
      );
    }
    @media (forced-colors: active) {
      td:not(:has(.item[aria-disabled])).selected {
        forced-color-adjust: none;
        color: ButtonFace;
      }
      td:not(:has(.item[aria-disabled])).selected .state-layer {
        background-color: ButtonText;
      }
      td:has(.item[aria-disabled]) {
        color: GrayText;
      }
      td.current:not(.selected):not(.special):not(.range-start):not(.range-end) {
        color: ButtonText;
      }
      td.current:not(.selected):not(.special):not(.range-start):not(.range-end) .state-layer {
        border-color: ButtonText;
      }
    }
  `;

  /** @private */ @query(".active > .item") private readonly _activeItem?: HTMLElement;

  /** Whether the view is active. */
  @property({ type: Boolean }) active = false;

  /** Today's date. */
  @property({ converter: dateConverter }) today: Date = new Date();

  /** The selected date. */
  @property({ converter: dateConverter }) date: Date | null = null;

  /** The active date. */
  @property({ attribute: "active-date", converter: dateConverter }) activeDate: Date = new Date();

  /** The minimum date that can be selected. */
  @property({ attribute: "min-date", converter: dateConverter }) minDate: Date | null = null;

  /** The maximum date that can be selected. */
  @property({ attribute: "max-date", converter: dateConverter }) maxDate: Date | null = null;

  /**
   * Asynchronously focuses the active date.
   * @returns {Promise<void>} A promise that resolves after the active date has been focused.
   */
  async focusActiveCell(): Promise<void> {
    if (this.isUpdatePending) {
      await this.updateComplete;
    }

    if (this._activeItem) {
      await focusWhenReady(this._activeItem);
    }
  }

  /** @internal */
  protected _changeActiveDate(activeDate: Date): void {
    activeDate = clampDate(activeDate, this.minDate, this.maxDate);
    if (!sameDate(activeDate, this.activeDate)) {
      this._activeItem?.style.setProperty("--m3e-state-layer-duration", "0ms");
      this._activeItem?.blur();
      this._activeItem?.style.removeProperty("--m3e-state-layer-duration");

      this.activeDate = activeDate;
      this.dispatchEvent(new Event("active-change", { bubbles: false }));
    }
  }
}
